import styled from "styled-components";
import { Check, X, Send, AlertTriangle, RotateCcw } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";
import { formatDateTime } from "./dateFormat";

// Confirmation for generating or sending a refund link.
//
// The plain confirm it replaces asked "are you sure" without answering the two questions the front
// desk actually has: WHERE is this going, and HOW MANY tries are left. Without those, a guest saying
// "it never arrived" produced button-mashing — four attempts burned against a number nobody had
// checked, and no record of where any of them went.
const Backdrop = styled.div`
  align-items: center;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 1600;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  max-width: 520px;
  width: 100%;
`;

const Head = styled.div`
  align-items: center;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  gap: 0.45rem;
  padding: 0.85rem 1.1rem;
`;

const Title = styled.h3`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  flex: 1 1 auto;
  font-size: 1rem;
  font-weight: 800;
  gap: 0.4rem;
  margin: 0;
`;

const Close = styled.button`
  background: transparent;
  border: 0;
  color: ${theme.ink700};
  cursor: pointer;
  padding: 0.1rem;
`;

const Body = styled.div`
  overflow-y: auto;
  padding: 0.85rem 1.1rem;
`;

// The destination is the point of the dialog, so it is the biggest thing in it.
const Destination = styled.div`
  background: ${theme.goldSoft};
  border: 1px solid ${theme.gold};
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
`;

const DestLabel = styled.div`
  color: ${theme.ink700};
  font-size: 0.73rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const DestValue = styled.div`
  color: ${theme.ink};
  font-size: 1.05rem;
  font-weight: 800;
  margin-top: 0.15rem;
  word-break: break-all;
`;

const DestHint = styled.div`
  color: #4a4f63;
  font-size: 0.76rem;
  font-weight: 600;
  margin-top: 0.2rem;
`;

const Line = styled.div`
  color: ${theme.ink700};
  font-size: 0.85rem;
  line-height: 1.5;
  margin-top: 0.7rem;
`;

const Budget = styled.div`
  background: ${(props) => (props.$exhausted ? theme.dangerBg : "#f4f2ee")};
  border-radius: 9px;
  color: ${(props) => (props.$exhausted ? theme.danger : theme.ink700)};
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1.5;
  margin-top: 0.7rem;
  padding: 0.55rem 0.7rem;
`;

const Warn = styled.div`
  align-items: flex-start;
  background: #fdf3e3;
  border-radius: 9px;
  color: #8a5a00;
  display: flex;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 0.35rem;
  line-height: 1.45;
  margin-top: 0.6rem;
  padding: 0.5rem 0.65rem;
`;

const AttemptsTitle = styled.div`
  color: ${theme.ink700};
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin: 0.9rem 0 0.3rem;
  text-transform: uppercase;
`;

const Attempt = styled.div`
  border-bottom: 1px solid #f1efe9;
  display: flex;
  gap: 0.6rem;
  justify-content: space-between;
  padding: 0.4rem 0;

  &:last-child {
    border-bottom: 0;
  }
`;

const AttemptMain = styled.div`
  min-width: 0;
`;

const AttemptWhere = styled.div`
  color: ${theme.ink};
  font-size: 0.84rem;
  font-weight: 700;
  word-break: break-all;
`;

const AttemptMeta = styled.div`
  color: #5e6278;
  font-size: 0.75rem;
  margin-top: 0.1rem;
`;

const Status = styled.span`
  border-radius: 999px;
  color: ${(props) => (props.$ok ? "#1b6e56" : theme.danger)};
  background: ${(props) => (props.$ok ? "#e6f6ee" : theme.dangerBg)};
  flex: 0 0 auto;
  font-size: 0.72rem;
  font-weight: 800;
  height: fit-content;
  padding: 0.15rem 0.5rem;
`;

const Empty = styled.div`
  color: #5e6278;
  font-size: 0.82rem;
`;

const Foot = styled.div`
  border-top: 1px solid ${theme.border};
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.75rem 1.1rem;
`;

const Btn = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.86rem;
  font-weight: 800;
  gap: 0.3rem;
  padding: 0.5rem 0.95rem;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Ghost = styled(Btn)`
  background: #ffffff;
  border: 1px solid ${theme.border};
  color: ${theme.ink700};
`;

const Ok = styled(Btn)`
  background: ${theme.gold};
  border: 0;
  color: #ffffff;

  &:hover:not(:disabled) {
    background: ${theme.goldDark};
  }
`;

// props: { mode: "send" | "generate", channel, refund, onConfirm, onCancel }
const RefundSendDialog = (props) => {
  const { t, lang } = useT();
  const refund = props.refund || {};
  const sending = props.mode === "send";
  const isEmail = props.channel === "email";

  const used = Number(refund.fo_attempts_used || 0);
  const max = Number(refund.fo_attempts_max || 0);
  const exhausted = max > 0 && used >= max;
  const destination = isEmail ? refund.send_to_email : refund.send_to_phone;
  const attempts = refund.attempts || [];

  const cooldownLeft = () => {
    if (!refund.cooldown_until) return 0;
    const ms = Date.parse(refund.cooldown_until) - Date.now();
    return ms > 0 ? Math.ceil(ms / 1000) : 0;
  };
  const waiting = sending ? cooldownLeft() : 0;

  return (
    <Backdrop onClick={props.onCancel}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>
            {sending ? <Send size={16} /> : <RotateCcw size={16} />}
            {sending
              ? `${t("refund_send_title")} (${isEmail ? "Email" : "WhatsApp"})`
              : t("refund_generate_title")}
          </Title>
          <Close type="button" aria-label={t("close")} onClick={props.onCancel}>
            <X size={18} />
          </Close>
        </Head>

        <Body>
          {sending ? (
            <Destination>
              <DestLabel>
                {isEmail ? t("refund_dest_email") : t("refund_dest_phone")}
              </DestLabel>
              <DestValue>{destination || "—"}</DestValue>
              <DestHint>{t("refund_dest_hint")}</DestHint>
            </Destination>
          ) : (
            <Line>{t("refund_generate_body")}</Line>
          )}

          {sending && !destination ? (
            <Warn>
              <AlertTriangle size={15} style={{ flex: "0 0 auto" }} />
              <span>{t("refund_no_destination")}</span>
            </Warn>
          ) : null}

          <Line>{t("refund_link_reused")}</Line>

          {sending ? (
            <Budget $exhausted={exhausted}>
              {t("refund_attempts_budget")
                .replace("%{used}", used)
                .replace("%{max}", max)
                .replace("%{left}", Math.max(0, max - used))}
              {exhausted ? ` ${t("refund_attempts_exhausted")}` : ""}
            </Budget>
          ) : null}

          {waiting > 0 ? (
            <Warn>
              <AlertTriangle size={15} style={{ flex: "0 0 auto" }} />
              <span>{t("refund_cooldown").replace("%{seconds}", waiting)}</span>
            </Warn>
          ) : null}

          <AttemptsTitle>{t("refund_previous_attempts")}</AttemptsTitle>
          {attempts.length === 0 ? (
            <Empty>{t("refund_no_attempts")}</Empty>
          ) : (
            attempts.map((attempt) => (
              <Attempt key={attempt.id}>
                <AttemptMain>
                  <AttemptWhere>{attempt.destination || "—"}</AttemptWhere>
                  <AttemptMeta>
                    {formatDateTime(attempt.at, lang)} ·{" "}
                    {attempt.channel === "email" ? "Email" : "WhatsApp"} ·{" "}
                    {attempt.actor === "auto"
                      ? t("refund_actor_auto")
                      : attempt.by || t(`refund_actor_${attempt.actor}`)}
                  </AttemptMeta>
                </AttemptMain>
                <Status $ok={attempt.delivered}>
                  {attempt.delivered
                    ? t("refund_attempt_sent")
                    : t("refund_attempt_failed")}
                </Status>
              </Attempt>
            ))
          )}
        </Body>

        <Foot>
          <Ghost type="button" onClick={props.onCancel}>
            <X size={15} /> {t("confirm_cancel")}
          </Ghost>
          <Ok
            type="button"
            disabled={sending && (exhausted || !destination || waiting > 0)}
            onClick={props.onConfirm}
          >
            <Check size={15} />{" "}
            {sending ? t("refund_send_ok") : t("refund_generate_ok")}
          </Ok>
        </Foot>
      </Card>
    </Backdrop>
  );
};

export default RefundSendDialog;
